import React, { useState } from 'react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import { useApp } from '../context/AppContext';
import { Plus, Trash2, Landmark } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useSort, SortableTh } from '../components/SortableTable';
import TableSearch, { rowMatches } from '../components/TableSearch';

export default function SourceMaster() {
  const { sources, addSource, updateSource, deleteSource, holdings } = useApp();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const { sort, onSort, sortRows } = useSort({ key: 'name', dir: 'asc' });
  const [query, setQuery] = useState('');

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error('Source name is required'); return; }
    addSource({ name: trimmed, active: true });
    setOpen(false);
    setName('');
    toast.success(`Added source: ${trimmed}`);
  };

  const countRowsForSource = (sid) => {
    let n = 0;
    Object.values(holdings).forEach((bySource) => {
      n += (bySource[sid] || []).length;
    });
    return n;
  };

  const sorted = sortRows(sources.filter((s) => rowMatches(s, query)), {
    name: (s) => s.name,
    active: (s) => (s.active ? 1 : 0),
  });

  return (
    <>
      <PageHeader
        overline="Masters"
        title="Source Master"
        description="Top-level sources (e.g. Interactive Brokers, HDFC Bank, Zerodha). Institutions are picked up automatically from Data Entry — you don't need to manage them here."
        actions={
          <button className="btn-primary" onClick={() => setOpen(true)} data-testid="add-source-button">
            <Plus size={14} /> Add source
          </button>
        }
      />

      <TableSearch
        value={query}
        onChange={setQuery}
        placeholder="Search by source name…"
        testId="sources-search"
        stats={query ? `${sorted.length} / ${sources.length} sources` : null}
      />

      <div className="border border-border bg-white">
        <table className="grid-table" data-testid="sources-table">
          <thead>
            <tr>
              <SortableTh sortKey="name" sort={sort} onSort={onSort}>Source</SortableTh>
              <SortableTh sortKey="active" sort={sort} onSort={onSort} className="w-[140px]">Active</SortableTh>
              <th className="w-[80px]" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((src) => (
              <tr key={src.id}>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 border border-border flex items-center justify-center bg-[#F6F6F4] shrink-0">
                      <Landmark size={14} className="text-[hsl(var(--primary))]" />
                    </div>
                    <div>
                      <div className="font-heading font-semibold">{src.name}</div>
                      <div className="text-[11px] text-[#787872] font-mono mt-0.5">
                        {src.institutions?.length || 0} institution{(src.institutions?.length || 0) === 1 ? '' : 's'} · {countRowsForSource(src.id).toLocaleString('en-IN')} rows
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={src.active}
                      onCheckedChange={(v) => updateSource(src.id, { active: v })}
                      data-testid={`toggle-active-${src.id}`}
                    />
                    <span className={`text-xs font-heading font-semibold ${src.active ? 'text-[hsl(var(--positive))]' : 'text-[#AAA]'}`}>
                      {src.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </td>
                <td>
                  {(() => {
                    const usage = countRowsForSource(src.id);
                    const blockedTitle = `Cannot delete "${src.name}" — in use across ${usage.toLocaleString('en-IN')} holding row${usage === 1 ? '' : 's'}. Clear those rows from Data Entry first.`;
                    if (usage > 0) {
                      return (
                        <button
                          onClick={() => toast.error(blockedTitle)}
                          className="text-[#CCC] cursor-not-allowed inline-flex p-1.5"
                          title={blockedTitle}
                          data-testid={`delete-source-${src.id}-blocked`}
                        >
                          <Trash2 size={13} />
                        </button>
                      );
                    }
                    return (
                      <button
                        onClick={async () => {
                          if (!confirm(`Delete source "${src.name}"?`)) return;
                          try { await deleteSource(src.id); toast.success(`Deleted ${src.name}`); }
                          catch (e) { toast.error(e.message || 'Delete failed'); }
                        }}
                        className="btn-ghost text-[#9B2C2C] hover:bg-[#F5E4E4]"
                        title={`Delete source "${src.name}"`}
                        data-testid={`delete-source-${src.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-source-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Add source</DialogTitle>
            <DialogDescription>
              Just the name. Institutions under this source will be added automatically as you enter data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="overline">Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                placeholder="e.g. Axis Bank"
                data-testid="source-name-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <button className="btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={submit} data-testid="confirm-add-source">Create source</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
